import { lazy, Suspense, Component, type ReactNode, type ErrorInfo } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { IS_CLOUD } from './config/app-role'
import { Toaster } from 'sonner' 
// deploy trigger 

// ─── Error Boundary ─────────────────────────────────────────────────────────
class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack)
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center p-8">
          <div className="max-w-lg space-y-4 text-center">
            <h2 className="text-lg font-semibold text-destructive">Bir hata oluştu / An error occurred</h2>
            <pre className="whitespace-pre-wrap text-xs text-left bg-muted p-4 rounded-lg overflow-auto max-h-60">
              {this.state.error?.message}
              {'\n\n'}
              {this.state.error?.stack}
            </pre>
            <button
              onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload() }}
              className="btn-primary text-sm"
            >
              Sayfayı Yenile / Reload
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
import { ProtectedRoute } from './components/layout/ProtectedRoute'
import { AppLayout } from './components/layout/AppLayout'
import LoginPage from './pages/auth/LoginPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'
import ResetPasswordPage from './pages/auth/ResetPasswordPage'
import ActivatePage from './pages/auth/ActivatePage'
import { QuitGuard } from './desktop/QuitGuard'
import { UpdaterModal } from './desktop/UpdaterModal'
import { api } from './lib/api'

/** Çıkış uyarısı için Veri Merkezine bağlı uzak cihaz sayısı (giriş yapılmamışsa 0) */
const countConnectedDevices = (): Promise<number> =>
  api.get<{ data: { connected: { remoteDevices: number } } }>('/desktop/status').then((r) => r.data.data.connected.remoteDevices)


// Code-split — sadece ilk render'da yüklenir
const TenantUsersPage = lazy(() => import('./pages/super/TenantUsersPage'))
const SuperTenantDataPage = lazy(() => import('./pages/super/SuperTenantDataPage'))
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'))
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'))
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'))
const ProposalsPage = lazy(() => import('./pages/proposals/ProposalsPage'))
const ProposalDetailPage = lazy(() => import('./pages/proposals/ProposalDetailPage'))
const ReceivedProposalDetailPage = lazy(() => import('./pages/procurement/ReceivedProposalDetailPage'))
const CustomersPage = lazy(() => import('./pages/customers/CustomersPage'))
const CustomerDetailPage = lazy(() => import('./pages/customers/CustomerDetailPage'))
const ProjectsPage = lazy(() => import('./pages/projects/ProjectsPage'))
const ProjectDetailPage = lazy(() => import('./pages/projects/ProjectDetailPage'))
const ProjectTemplatesPage = lazy(() => import('./pages/projects/ProjectTemplatesPage'))
const TimesheetsPage = lazy(() => import('./pages/timesheets/TimesheetsPage'))
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'))
const NotificationsPage = lazy(() => import('./pages/notifications/NotificationsPage'))
const UsersPage = lazy(() => import('./pages/users/UsersPage'))
const UserDetailPage = lazy(() => import('./pages/users/UserDetailPage'))
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'))
const FinancePage = lazy(() => import('./pages/finance/FinancePage'))
const FinanceOverviewPage = lazy(() => import('./pages/finance/FinanceOverviewPage'))
const EmployeeDetailPage = lazy(() => import('./pages/finance/EmployeeDetailPage'))
const PayrollPage = lazy(() => import('./pages/finance/PayrollPage'))
const ProgressMonitorPage = lazy(() => import('./pages/monitor/ProgressMonitorPage'))
const SubConsultantsPage = lazy(() => import('./pages/sub-consultants/SubConsultantsPage'))
const SubConsultantDetailPage = lazy(() => import('./pages/sub-consultants/SubConsultantDetailPage'))
const SubConsultantContractDetailPage = lazy(() => import('./pages/sub-consultants/SubConsultantContractDetailPage'))
const RevenuePage = lazy(() => import('./pages/revenue/RevenuePage'))
const CustomerHakedisDetailPage = lazy(() => import('./pages/revenue/CustomerHakedisDetailPage'))
const SubConsultantHakedisDetailPage = lazy(() => import('./pages/sub-consultants/SubConsultantHakedisDetailPage'))
const DataEntryPage = lazy(() => import('./pages/data-entry/DataEntryPage'))
const AiWorkspacePage = lazy(() => import('./pages/ai/AiWorkspacePage'))
const InstrumentsPage = lazy(() => import('./pages/instruments/InstrumentsPage'))
const LeavePage = lazy(() => import('./pages/leave/LeavePage'))

const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
)

export default function App(): JSX.Element {
  return (
    <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Genel rotalar */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/activate" element={<ActivatePage />} />
        {/* Bulut: açık kayıt (14 gün deneme) ve e-posta doğrulama */}
        {IS_CLOUD && <Route path="/register" element={<RegisterPage />} />}
        {IS_CLOUD && <Route path="/verify-email" element={<VerifyEmailPage />} />}

        {/* Bulut: Super Admin paneli Ayarlar'ın içindedir; alt sayfalar ve eski adres buraya döner */}
        {IS_CLOUD && <Route path="/super-admin" element={<Navigate to="/settings?tab=platform" replace />} />}
        {IS_CLOUD && <Route path="/super-admin/tenant-users" element={<TenantUsersPage />} />}
        {IS_CLOUD && <Route path="/super-admin/tenant/:tenantId/data" element={<SuperTenantDataPage />} />}

        {/* Monitor — AppLayout olmadan (tam ekran desteği) */}
        <Route element={<ProtectedRoute />}>
          <Route path="/monitor" element={<ProgressMonitorPage />} />
        </Route>

        {/* Korumalı rotalar */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/data-entry" element={<DataEntryPage />} />
            <Route path="/ai" element={<AiWorkspacePage />} />

            {/* İş Geliştirme */}
            <Route
              path="/proposals/*"
              element={<ProtectedRoute allowedRoles={['TENANT_ADMIN', 'PM', 'CR']} />}
            >
              <Route index element={<ProposalsPage />} />
              <Route path=":id" element={<ProposalDetailPage />} />
              <Route path="received/:id" element={<ReceivedProposalDetailPage />} />
            </Route>
            <Route
              path="/customers"
              element={<ProtectedRoute allowedRoles={['TENANT_ADMIN', 'PM', 'CR']} />}
            >
              <Route index element={<CustomersPage />} />
              <Route path=":id" element={<CustomerDetailPage />} />
            </Route>

            {/* Proje Yönetimi */}
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/templates" element={<ProjectTemplatesPage />} />
            <Route path="/projects/:id" element={<ProjectDetailPage />} />
            <Route path="/timesheets/*" element={<TimesheetsPage />} />
            <Route path="/leave" element={<LeavePage />} />

            {/* Finans */}
            <Route
              path="/finance/overview"
              element={<ProtectedRoute allowedRoles={['TENANT_ADMIN', 'ACCOUNTANT', 'PM', 'MGMT']} />}
            >
              <Route index element={<FinanceOverviewPage />} />
            </Route>
            <Route
              path="/revenue"
              element={<ProtectedRoute allowedRoles={['TENANT_ADMIN', 'ACCOUNTANT', 'PM', 'MGMT']} />}
            >
              <Route index element={<RevenuePage />} />
              <Route path="hakedis/:hakedisId" element={<CustomerHakedisDetailPage />} />
            </Route>
            <Route
              path="/finance/*"
              element={<ProtectedRoute allowedRoles={['TENANT_ADMIN', 'ACCOUNTANT']} />}
            >
              <Route index element={<FinancePage />} />
            </Route>
            <Route
              path="/finance/employees/:id"
              element={<ProtectedRoute allowedRoles={['TENANT_ADMIN', 'ACCOUNTANT']} />}
            >
              <Route index element={<EmployeeDetailPage />} />
            </Route>
            <Route
              path="/finance/payroll"
              element={<ProtectedRoute allowedRoles={['TENANT_ADMIN', 'ACCOUNTANT']} />}
            >
              <Route index element={<PayrollPage />} />
            </Route>
            <Route
              path="/sub-consultants"
              element={<ProtectedRoute allowedRoles={['TENANT_ADMIN', 'PM', 'ACCOUNTANT']} />}
            >
              <Route index element={<SubConsultantsPage />} />
              <Route path=":id" element={<SubConsultantDetailPage />} />
              <Route path=":vendorId/contracts/:contractId" element={<SubConsultantContractDetailPage />} />
              <Route path=":vendorId/contracts/:contractId/hakedis/:hakedisId" element={<SubConsultantHakedisDetailPage />} />
            </Route>
            <Route
              path="/instruments"
              element={<ProtectedRoute allowedRoles={['TENANT_ADMIN', 'ACCOUNTANT', 'PM', 'MGMT']} />}
            >
              <Route index element={<InstrumentsPage />} />
            </Route>

            {/* Diğer */}
            <Route
              path="/reports/*"
              element={<ProtectedRoute allowedRoles={['TENANT_ADMIN', 'PM', 'MGMT', 'ACCOUNTANT']} />}
            >
              <Route index element={<ReportsPage />} />
            </Route>
            <Route path="/notifications" element={<NotificationsPage />} />

            {/* Yönetim */}
            <Route
              path="/users"
              element={<ProtectedRoute allowedRoles={['TENANT_ADMIN']} />}
            >
              <Route index element={<UsersPage />} />
              <Route path=":id" element={<UserDetailPage />} />
            </Route>
            <Route
              path="/settings"
              element={<ProtectedRoute allowedRoles={['TENANT_ADMIN']} />}
            >
              <Route index element={<SettingsPage />} />
            </Route>
          </Route>
        </Route>


        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
    {/* Masaüstü: tray'den çıkışta bağlı kullanıcı uyarısı */}
    <QuitGuard connectedClients={countConnectedDevices} />
    <UpdaterModal />
    <Toaster position="top-right" richColors closeButton />
    </ErrorBoundary>
  )
}
